import { CalendarDays, MapPin, MonitorPlay, Plus, Wrench } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { fmtDate } from '../../../shared/format'
import { INSTALLATION_STATUSES, type Installation, type InstallationStatus, type Project, type Stage } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { useInstallationActions, useSaveInstallation } from '../../workApi'
import { ProgressBar } from '../ProjectBits'
import { InstallationKindBadge } from '../StatusBadges'
import { StageFormModal } from './StageFormModal'
import { StageStepper } from './StageStepper'
import { StageTable } from './StageTable'

type Props = { project: Project; installation: Installation; canWrite: boolean }

export function InstallationDetail({ project, installation: inst, canWrite }: Props) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const save = useSaveInstallation(project.id)
  const actions = useInstallationActions(project.id, inst.id)
  const [stageDialog, setStageDialog] = useState<{ open: boolean; stage: Stage | null }>({ open: false, stage: null })
  const Icon = inst.kind === 'installation' ? Wrench : MonitorPlay

  const changeStatus = async (status: InstallationStatus) => {
    const ok = await confirmModal({
      title: t('projects.install.statusTitle'),
      description: t('projects.install.statusBody', { name: inst.customer, to: t(`projects.installStatus.${status}`) }),
      confirmLabel: t('common.confirm'),
    })
    if (!ok) return
    const { accountId, kind, ownerId, startDate, targetDate } = inst
    save.mutate({ id: inst.id, input: { accountId, kind, ownerId, startDate, targetDate, status } }, { onSuccess: () => toast.success(t('projects.install.updated')), onError: toastError })
  }

  const onDeleteStage = async (s: Stage) => {
    const ok = await confirmModal({ title: t('projects.install.stageDeleteTitle'), description: t('projects.install.stageDeleteBody', { name: s.name, customer: inst.customer }), confirmLabel: t('common.delete'), destructive: true })
    if (!ok) return
    actions.deleteStage.mutate(s.id, { onSuccess: () => toast.success(t('projects.install.stageDeleted')), onError: toastError })
  }

  return (
    <section className="@container overflow-hidden rounded-xl border bg-card shadow-card">
      <header className="flex flex-col gap-4 border-b p-4 sm:p-5 @4xl:flex-row @4xl:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <span className="flex size-14 shrink-0 items-center justify-center rounded-full border bg-muted/40">
            <Icon className="size-6 text-muted-foreground" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold">{inst.customer}</h3>
              <InstallationKindBadge kind={inst.kind} />
            </div>
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="size-3.5" aria-hidden />
              {inst.city || '—'} · {inst.ownerId ? nameOf(inst.ownerId) : t('projects.work.unassigned')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 @xl:grid-cols-3 @4xl:w-[34rem] @4xl:shrink-0">
          <div>
            <div className="text-[11px] text-muted-foreground">{t('projects.install.columns.target')}</div>
            <div className="flex items-center gap-1.5 font-semibold tabular-nums">
              <CalendarDays className="size-4 text-muted-foreground" aria-hidden />
              {fmtDate(inst.targetDate)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">{t('projects.install.overall')}</div>
            <ProgressBar value={inst.progress} label={t('projects.install.overall')} />
          </div>
          <div className="col-span-2 @xl:col-span-1">
            <div className="text-[11px] text-muted-foreground">{t('projects.install.columns.status')}</div>
            {canWrite ? (
              <Select size="sm" value={inst.status} onChange={(v) => void changeStatus(v)} aria-label={t('projects.install.columns.status')} options={INSTALLATION_STATUSES.map((s) => ({ value: s, label: t(`projects.installStatus.${s}`) }))} />
            ) : (
              <span className="text-sm font-medium">{t(`projects.installStatus.${inst.status}`)}</span>
            )}
          </div>
        </div>
      </header>

      <div className="space-y-4 p-4 sm:p-5">
        <StageStepper stages={inst.stages} />
        <div className="flex items-center gap-2">
          <h4 className="font-semibold">{t('projects.install.stages')}</h4>
          {canWrite ? (
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => setStageDialog({ open: true, stage: null })}>
              <Plus />
              {t('projects.install.addStage')}
            </Button>
          ) : null}
        </div>
        {inst.stages.length === 0 ? (
          <p className="rounded-xl border border-dashed py-8 text-center text-sm text-muted-foreground">{t('projects.install.noStages')}</p>
        ) : (
          <StageTable project={project} stages={inst.stages} canWrite={canWrite} onEdit={(stage) => setStageDialog({ open: true, stage })} onDelete={(stage) => void onDeleteStage(stage)} />
        )}
      </div>
      <StageFormModal open={stageDialog.open} project={project} installation={inst} stage={stageDialog.stage} onOpenChange={(open) => setStageDialog((s) => ({ ...s, open }))} />
    </section>
  )
}
